import React, { useMemo, useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { ExpandMoreOutlined } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import PrimaryBtn from 'components/shared/Buttons/PrimaryButton';

import { yupResolver } from '@hookform/resolvers/yup';
import { getDefaultRefundFormValues } from './constants/defaultRefundForm';

import type RefundForm from './models/RefundForm';

import { useTranslation } from 'react-i18next';

import useSubmitForm from './hooks/useSubmitForm';
import refundSchema from './schemas/refundSchema';
import STEPS from './steps.config';

const Refund: React.FC = () => {
    const { t } = useTranslation('refund');

    const theme = useTheme();

    const small = useMediaQuery(theme.breakpoints.down('md'));

    const form = useForm<RefundForm>({
        mode: 'onChange',
        resolver: yupResolver(refundSchema(t)),
        defaultValues: getDefaultRefundFormValues(),
    });

    const [expanded, setExpanded] = useState<number>(0);

    const handleExpansion = (index: number): void => {
        if (index === expanded) return setExpanded(null);
        setExpanded(index);
    };

    const handleNext = async (fields: (keyof RefundForm)[]): Promise<void> => {
        if (!(await form.trigger(fields))) return;

        setExpanded((current) => current + 1);
    };

    const steps = useMemo(() => STEPS(t), [t]);

    const { handleSubmit, loading } = useSubmitForm(() => {
        form.reset();
        setExpanded(0);
    });

    return (
        <Stack gap={1} paddingX={small ? 0 : 2}>
            <Typography variant="h2" textTransform="uppercase" marginBottom={2}>
                {t('TITLE')}
            </Typography>

            <FormProvider {...form}>
                {steps.map((step, key) => (
                    <Accordion
                        key={key}
                        expanded={expanded === key}
                        onChange={handleExpansion.bind(this, key)}
                        variant="outlined"
                        sx={{ border: '1px solid #ececec' }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreOutlined color="primary" />}
                            disabled={key > expanded}
                        >
                            <Typography
                                variant="subtitle1"
                                textTransform="uppercase"
                            >
                                {key + 1}. {step.title}
                            </Typography>
                        </AccordionSummary>

                        <AccordionDetails sx={{ backgroundColor: '#f4f4f4' }}>
                            <Stack paddingY={1}>
                                <Typography variant="subtitle2">
                                    {step.description}
                                </Typography>

                                <Stack paddingY={4}>
                                    {React.createElement(step.Component)}
                                </Stack>

                                <PrimaryBtn
                                    onClick={
                                        key + 1 === steps.length
                                            ? form.handleSubmit(handleSubmit)
                                            : handleNext.bind(this, step.fields)
                                    }
                                    disabled={
                                        Object.keys(form.formState.errors)
                                            .length > 1 || loading
                                    }
                                    sx={{
                                        width: small ? '100%' : 'fit-content',
                                    }}
                                >
                                    {step.nextButtonLabel}
                                </PrimaryBtn>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </FormProvider>
        </Stack>
    );
};

export default Refund;
